import { useState } from "react";
import { Link } from "wouter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Quote } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Search,
  Calculator,
  Send,
  FileText,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Eye,
  Loader2,
  Clock,
  Upload,
  Share2,
  CheckCircle2,
  ArrowRight,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePermissions } from "@/hooks/usePermissions";
import { MarkAcceptedDialog } from "./MarkAcceptedDialog";

interface QuotesListProps {
  onNewQuote: () => void;
  onNewRFP: () => void;
  onEditQuote: (quote: Quote) => void;
  canCreateRfp?: boolean;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "draft":
      return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
    case "sent":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
    case "accepted":
      return "status-active";
    case "rejected":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
    default:
      return "status-inactive";
  }
};

const getPlanTypeLabel = (planType: string) => {
  switch (planType) {
    case "health":
      return "Health Only";
    case "dental":
      return "Dental Only";
    case "vision":
      return "Vision Only";
    case "full_package":
      return "Full Package";
    default:
      return planType;
  }
};

const formatDate = (dateInput: string | Date | null) => {
  if (!dateInput) return "N/A";
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  return d.toLocaleDateString();
};

type SortColumn = "company" | "type" | "planType" | "employees" | "status" | "created";
type SortDirection = "asc" | "desc";

export function QuotesList({ onNewQuote, onNewRFP, onEditQuote, canCreateRfp = true }: QuotesListProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all"); // 'all', 'quote', 'rfp'
  const [sortColumn, setSortColumn] = useState<SortColumn>("created");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState<Quote | null>(null);
  // A quote the broker is recording as accepted off the platform.
  const [quoteToAccept, setQuoteToAccept] = useState<Quote | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { can } = usePermissions();

  const { data: quotes = [], isLoading } = useQuery<Quote[]>({
    queryKey: ["/api/quotes"],
  });

  const updateQuoteMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Quote> }) => {
      return apiRequest("PUT", `/api/quotes/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({
        title: "Success",
        description: "Quote updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update quote",
        variant: "destructive",
      });
    },
  });

  const deleteQuoteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/quotes/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      toast({
        title: "Success",
        description: "Quote deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete quote",
        variant: "destructive",
      });
    },
  });

  const handleStatusUpdate = async (quote: Quote, newStatus: string) => {
    // Reverting to Draft (to re-edit) strands a client's pending submission —
    // warn, then discard it so it doesn't haunt the revised proposal.
    if ((quote as any).hasPendingResponse && newStatus === "draft") {
      const ok = window.confirm(
        "This client has submitted selections awaiting your confirmation. Reverting to Draft will discard those selections. Continue?",
      );
      if (!ok) return;
      try {
        await apiRequest("DELETE", `/api/quotes/${quote.id}/acceptance`);
      } catch {
        toast({
          title: "Couldn't discard the submission",
          description: "Please try again.",
          variant: "destructive",
        });
        return;
      }
    }
    updateQuoteMutation.mutate({
      id: quote.id,
      data: { status: newStatus },
    });
  };

  // Open the client-facing shared quote page. Generate a share link first if
  // the quote doesn't have one yet (e.g. finalized without clicking "share").
  const [openingSharedId, setOpeningSharedId] = useState<number | null>(null);
  const handleViewShared = async (quote: Quote) => {
    let token = (quote as any).shareToken as string | null;
    if (!token) {
      setOpeningSharedId(quote.id);
      try {
        const res = await apiRequest("POST", `/api/quotes/${quote.id}/share`, {
          proposalSettings: (quote as any).proposalSettings || undefined,
        });
        if (!res.ok) throw new Error("Failed to create share link");
        token = (await res.json()).shareToken;
        queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      } catch (error) {
        console.error("Failed to open shared quote:", error);
        toast({
          title: "Couldn't open the shared quote",
          description: "Please try again.",
          variant: "destructive",
        });
        return;
      } finally {
        setOpeningSharedId(null);
      }
    }
    window.open(`/quote/${token}`, "_blank", "noopener,noreferrer");
  };

  const handleDeleteClick = (quote: Quote) => {
    setQuoteToDelete(quote);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (quoteToDelete) {
      deleteQuoteMutation.mutate(quoteToDelete.id);
    }
    setDeleteDialogOpen(false);
    setQuoteToDelete(null);
  };

  // Helper to get quote type (from type field or wizardState.mode for legacy records)
  const getQuoteType = (quote: Quote): string => {
    if ((quote as any).type) return (quote as any).type;
    // Fall back to wizardState.mode for existing records
    const ws = (quote as any).wizardState;
    return ws?.mode || "quote";
  };

  /**
   * The one action a row is for. Order matters: a draft RFP must match "draft"
   * before "rfp", or a half-finished RFP offers "Compare proposals" and can
   * never be resumed.
   */
  const primaryAction = (quote: Quote) => {
    const isRfp = getQuoteType(quote) === "rfp";

    if ((quote as any).hasPendingResponse) {
      return (
        <PermissionButton permission="quotes:view" whenDenied="hide" variant="outline" size="sm" className="border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/40" asChild>
          <Link href={`/quoting/accepted/${quote.id}`}>
            <Clock className="h-4 w-4 mr-1" />
            Review response
          </Link>
        </PermissionButton>
      );
    }

    if (quote.status === "accepted") {
      // Acceptances live in two places: a quote's in the quote acceptance
      // table, an RFP's on rfp_compiled_proposals. Sending an accepted RFP to
      // the quote page reads the wrong one and always says "No response yet".
      return (
        <PermissionButton permission="quotes:view" whenDenied="hide" variant="outline" size="sm" asChild>
          <Link href={isRfp ? `/quoting/rfp/${quote.id}/proposals` : `/quoting/accepted/${quote.id}`}>
            <ArrowRight className="h-4 w-4 mr-1" />
            Set up plans
          </Link>
        </PermissionButton>
      );
    }

    if (quote.status === "draft") {
      return (
        <PermissionButton permission="quotes:edit" whenDenied="hide" variant="outline" size="sm" onClick={() => onEditQuote(quote)}>
          Resume
        </PermissionButton>
      );
    }

    if (isRfp) {
      return (
        <PermissionButton permission="quotes:view" whenDenied="hide" variant="outline" size="sm" className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300" asChild>
          <Link href={`/quoting/rfp/${quote.id}/proposals`}>
            <Upload className="h-4 w-4 mr-1" />
            Compare proposals
          </Link>
        </PermissionButton>
      );
    }

    // Finalizing sets status "sent", but nothing was sent: sharing is a
    // separate act that writes a published link and never touches status. So
    // the label depends on whether a link exists, rather than assuming the
    // client has seen it.
    return (
      <PermissionButton permission="quotes:view" whenDenied="hide" variant="outline" size="sm" disabled={openingSharedId === quote.id} onClick={() => handleViewShared(quote)}>
        {openingSharedId === quote.id ? (
          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
        ) : (quote as any).hasSharedLink ? (
          <Eye className="h-4 w-4 mr-1" />
        ) : (
          <Share2 className="h-4 w-4 mr-1" />
        )}
        {(quote as any).hasSharedLink ? "View shared quote" : "Share"}
      </PermissionButton>
    );
  };

  /** Everything else this row can do, Delete last and set apart. */
  const rowMenuItems = (quote: Quote) => {
    const isRfp = getQuoteType(quote) === "rfp";
    const items: React.ReactNode[] = [];

    // Offered on drafts too — carriers sometimes reply before the RFP is
    // formally finished.
    if (isRfp && quote.status !== "accepted" && can("quotes:view")) {
      if (quote.status === "draft") {
        items.push(
          <DropdownMenuItem key="compare" asChild>
            <Link href={`/quoting/rfp/${quote.id}/proposals`}>
              <Upload className="h-4 w-4 mr-2" />
              Compare proposals
            </Link>
          </DropdownMenuItem>,
        );
      } else {
        items.push(
          <DropdownMenuItem key="sent" asChild>
            <Link href={`/quoting/rfp/${quote.id}`}>
              <Eye className="h-4 w-4 mr-2" />
              What was sent
            </Link>
          </DropdownMenuItem>,
        );
      }
    }

    // A client who says yes on the phone left the quote stuck at "sent" with no
    // way to set up their plans.
    if (!isRfp && quote.status === "sent" && can("quotes:edit")) {
      items.push(
        <DropdownMenuItem key="accept" onClick={() => setQuoteToAccept(quote)}>
          <CheckCircle2 className="h-4 w-4 mr-2" />
          Mark as accepted
        </DropdownMenuItem>,
      );
    }

    if (can("quotes:delete")) {
      if (items.length) items.push(<DropdownMenuSeparator key="sep" />);
      items.push(
        <DropdownMenuItem
          key="delete"
          className="text-destructive focus:text-destructive"
          onClick={() => handleDeleteClick(quote)}
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </DropdownMenuItem>,
      );
    }

    return items;
  };

  const toggleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortColumn !== column) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortDirection === "asc" 
      ? <ChevronUp className="h-4 w-4 ml-1" />
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  const filteredQuotes = quotes
    .filter((quote) => {
      const matchesSearch =
        quote.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        quote.planType.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === "all" || quote.status === filterStatus;
      const matchesType = filterType === "all" || getQuoteType(quote) === filterType;
      return matchesSearch && matchesStatus && matchesType;
    })
    .sort((a, b) => {
      const multiplier = sortDirection === "asc" ? 1 : -1;
      switch (sortColumn) {
        case "company":
          return multiplier * a.companyName.localeCompare(b.companyName, undefined, { sensitivity: "base" });
        case "type":
          return multiplier * getQuoteType(a).localeCompare(getQuoteType(b));
        case "planType":
          return multiplier * a.planType.localeCompare(b.planType);
        case "employees":
          return multiplier * ((a.employeeCount || 0) - (b.employeeCount || 0));
        case "status":
          return multiplier * a.status.localeCompare(b.status);
        case "created":
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return multiplier * (dateA - dateB);
        default:
          return 0;
      }
    });

  // Separate counts for quotes and RFPs
  const quoteRecords = quotes.filter((q) => getQuoteType(q) === "quote");
  const rfpRecords = quotes.filter((q) => getQuoteType(q) === "rfp");

  const quoteSummary = {
    totalQuotes: quoteRecords.length,
    totalRFPs: rfpRecords.length,
    sentQuotes: quoteRecords.filter((q) => q.status === "sent").length,
    acceptedQuotes: quoteRecords.filter((q) => q.status === "accepted").length,
    conversionRate:
      quoteRecords.filter((q) => q.status === "sent").length > 0
        ? (
            (quoteRecords.filter((q) => q.status === "accepted").length /
              quoteRecords.filter((q) => q.status === "sent").length) *
            100
          ).toFixed(1)
        : "0",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            Insurance Quoting
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Generate and manage insurance quotes for prospective clients
          </p>
        </div>
        <div className="flex gap-2">
          {canCreateRfp && (
            <PermissionButton
              permission="quotes:create"
              whenDenied="hide"
              onClick={onNewRFP}
              variant="outline"
            >
              <Plus className="h-4 w-4 mr-2" />
              New RFP
            </PermissionButton>
          )}
          <PermissionButton
            permission="quotes:create"
            whenDenied="hide"
            onClick={onNewQuote}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Quote
          </PermissionButton>
        </div>
      </div>

      {/* Quote Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="text-2xl text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Quotes
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {quoteSummary.totalQuotes}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Send className="text-2xl text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    RFPs
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {quoteSummary.totalRFPs}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Calculator className="text-2xl text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Accepted
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {quoteSummary.acceptedQuotes}
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="metrics-card">
          <CardContent className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="text-2xl text-purple-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Conversion Rate
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-gray-100">
                    {quoteSummary.conversionRate}%
                  </dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quote Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search quotes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="quote">Quotes</SelectItem>
                <SelectItem value="rfp">RFPs</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6">
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 bg-gray-200 rounded w-2/3 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => toggleSort("company")}
                    >
                      <div className="flex items-center">
                        Company
                        {getSortIcon("company")}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => toggleSort("type")}
                    >
                      <div className="flex items-center">
                        Type
                        {getSortIcon("type")}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => toggleSort("planType")}
                    >
                      <div className="flex items-center">
                        Plan Type
                        {getSortIcon("planType")}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => toggleSort("employees")}
                    >
                      <div className="flex items-center">
                        Employees
                        {getSortIcon("employees")}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => toggleSort("status")}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon("status")}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => toggleSort("created")}
                    >
                      <div className="flex items-center">
                        Created
                        {getSortIcon("created")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredQuotes.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <Calculator className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                            No quotes found
                          </h3>
                          <p className="text-gray-500 dark:text-gray-400 mb-4">
                            {searchTerm || filterStatus !== "all"
                              ? "Try adjusting your search or filters"
                              : "Get started by creating your first quote"}
                          </p>
                          <PermissionButton
                            permission="quotes:create"
                            whenDenied="hide"
                            onClick={onNewQuote}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create New Quote
                          </PermissionButton>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredQuotes.map((quote) => (
                      <tr
                        key={quote.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {quote.companyName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            variant="outline"
                            className={
                              getQuoteType(quote) === "rfp"
                                ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300"
                                : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                            }
                          >
                            {getQuoteType(quote) === "rfp" ? "RFP" : "Quote"}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge variant="outline" className="capitalize">
                            {getPlanTypeLabel(quote.planType)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {quote.employeeCount}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getQuoteType(quote) === "rfp" && quote.status === "sent" ? (
                            <div className="space-y-1">
                              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                Sent
                              </Badge>
                              <div className="text-xs text-muted-foreground">
                                {quote.updatedAt && formatDate(quote.updatedAt)}
                                {(quote as any).carrierIds?.length > 0 && (
                                  <span className="ml-1">
                                    • {(quote as any).carrierIds.length} carrier{(quote as any).carrierIds.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-1">
                              <Select
                                value={quote.status}
                                onValueChange={(newStatus) =>
                                  handleStatusUpdate(quote, newStatus)
                                }
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="draft">Draft</SelectItem>
                                  <SelectItem value="sent">Sent</SelectItem>
                                  <SelectItem value="accepted">Accepted</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                              {(quote as any).hasPendingResponse && (
                                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 text-xs">
                                  1 pending response
                                </Badge>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(quote.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {/* One primary action plus an overflow menu, so every
                              row is the same two controls wide. */}
                          <div className="flex items-center justify-end gap-2">
                            {primaryAction(quote)}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="px-2"
                                  aria-label={`More actions for ${quote.companyName}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {rowMenuItems(quote)}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the quote for{" "}
              <span className="font-semibold">{quoteToDelete?.companyName}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MarkAcceptedDialog
        quote={quoteToAccept}
        onOpenChange={(open) => !open && setQuoteToAccept(null)}
      />
    </div>
  );
}
